// Adapters from snapshot data shape into the room/program price dialog input shape
// (see @digitalopsdev/serviceops-reservations-components RoomPriceDialogComponent /
// ProgramPriceDialogComponent). Lets the snapshot UI render through the same dialog
// the rest of the app uses.

import type {
    CalculatedPrice,
    PriceBreakdownContainer,
    ReservationState,
    RoomBookingDate,
    RoomBookingState,
    ProgramBookingState,
} from '@digitalopsdev/serviceops-reservations-components';
import type { DayRateSnapshot, RoomRateSnapshot, ProgramRateSnapshot } from './types';

// Minimal modelState<T> stub. The dialogs only read `.value`; the subscription/dirty
// API is never exercised when the dialog is rendered for a static snapshot.
function stub<T>(value: T): any {
    return {
        value,
        oldValue: value,
        get isDirty() { return false; },
        setValue: () => {},
        commit: () => {},
        rollback: () => {},
        subscribe: () => () => {},
        unsubscribe: () => {},
    };
}

function emptyPriceBreakdown(grossAmount: number): PriceBreakdownContainer {
    const empty = { Gross: grossAmount, Vat: 0, ServiceCharge: 0, Net: grossAmount };
    return {
        RatePlanName: '',
        BaseAmount: { Amount: empty, Source: 'Base' },
        Multipliers: [],
        Discounts: [],
        AmountBeforeDiscount: empty,
        AmountAfterDiscount: empty,
    };
}

function dayRateToBookingDate(dayRate: DayRateSnapshot, displayDate?: Date): RoomBookingDate {
    const date = displayDate ?? new Date(dayRate.date + 'T00:00:00Z');
    const breakdown = dayRate.priceBreakdown ?? emptyPriceBreakdown(dayRate.grossAmount);
    const baseAmount = breakdown.AmountBeforeDiscount.Gross + dayRate.minorAdjustment;
    const payableAmount = breakdown.AmountAfterDiscount.Gross + dayRate.minorAdjustment;

    return {
        Date: date,
        VoucherEffects: [],
        PriceBreakdown: breakdown,
        SegmentIndex: stub(0),
        RecalculateStayPeriod: stub(null),
        RatePlanOverride: stub(null),
        BaseRateOverride: stub(null),
        UseOriginalRatePlan: stub(null),
        MinorAdjustment: stub(dayRate.minorAdjustment),
        AppliedVouchers: stub([]),
        NoShow: stub(false),
        NoCharge: stub(false),
        AdditionalCharges: stub([]),
        CalculatedPrice: {
            breakdown,
            totalBaseAmount: baseAmount,
            totalPayableAmount: payableAmount,
        },
    } as unknown as RoomBookingDate;
}

export function buildReservationStateForDialog(
    roomTypeKey: string | null,
    roomTypeName: string | null,
    programTypeKey: string | null,
    programTypeName: string | null,
): ReservationState {
    const roomTypes: Record<string, { Name: string }> = {};
    const programTypes: Record<string, { Name: string }> = {};
    if (roomTypeKey) roomTypes[roomTypeKey] = { Name: roomTypeName ?? roomTypeKey };
    if (programTypeKey) programTypes[programTypeKey] = { Name: programTypeName ?? programTypeKey };
    return { RoomTypes: roomTypes, ProgramTypes: programTypes } as unknown as ReservationState;
}

interface DialogDayInput {
    dayRate: DayRateSnapshot;
    displayDate?: Date;
}

function inputsToDates(inputs: DialogDayInput[]): RoomBookingDate[] {
    return inputs.map(i => dayRateToBookingDate(i.dayRate, i.displayDate));
}

function rangeFromDates(dates: Date[]): { start: Date; end: Date } {
    if (dates.length === 0) {
        const today = new Date();
        return { start: today, end: today };
    }
    const sorted = [...dates].sort((a, b) => a.getTime() - b.getTime());
    return { start: sorted[0], end: sorted[sorted.length - 1] };
}

function aggregateCalculatedPrice(dates: RoomBookingDate[]): CalculatedPrice {
    const baseAmount = dates.reduce((s, d) => s + d.CalculatedPrice.totalBaseAmount, 0);
    const payableAmount = dates.reduce((s, d) => s + d.CalculatedPrice.totalPayableAmount, 0);
    return {
        breakdown: emptyPriceBreakdown(payableAmount),
        totalBaseAmount: baseAmount,
        totalPayableAmount: payableAmount,
    };
}

function priceModelStub() {
    return {
        price: stub({ baseAmount: 0, payableAmount: 0 }),
        isLoading: stub(false),
    };
}

export function buildRoomBookingStateForDialog(
    roomTypeKey: string,
    inputs: DialogDayInput[],
): RoomBookingState {
    const dates = inputsToDates(inputs);
    const { start, end } = rangeFromDates(dates.map(d => d.Date));
    const checkOut = new Date(end);
    checkOut.setUTCDate(checkOut.getUTCDate() + 1);
    return {
        Dates: dates,
        CheckIn: stub(start),
        CheckOut: stub(checkOut),
        RoomType: stub(roomTypeKey),
        CalculatedPrice: stub(aggregateCalculatedPrice(dates)),
        Price: priceModelStub(),
        NoCharge: stub(false),
    } as unknown as RoomBookingState;
}

export function buildProgramBookingStateForDialog(
    programTypeKey: string,
    inputs: DialogDayInput[],
): ProgramBookingState {
    const dates = inputsToDates(inputs);
    const { start, end } = rangeFromDates(dates.map(d => d.Date));
    return {
        Dates: dates,
        Start: stub(start),
        End: stub(end),
        ProgramType: stub(programTypeKey),
        CalculatedPrice: stub(aggregateCalculatedPrice(dates)),
        Price: priceModelStub(),
        NoCharge: stub(false),
    } as unknown as ProgramBookingState;
}

export function inputsFromSnapshotItem(item: RoomRateSnapshot | ProgramRateSnapshot): DialogDayInput[] {
    return item.dayRates.map(d => ({ dayRate: d }));
}
